import styled from "@emotion/styled/macro";
import {
  Button,
  Checkbox,
  Textarea,
  tokens,
  Typography,
  Banner,
  BannerTypes,
} from "@sunrun/experience-ui-components";
import { useCallback, useMemo, useState } from "react";
import { ListOffer } from "../../../amplify/backend/function/offerexpstoreFrontApi/ts/public/offerTypes";
import {
  sendProposalEmail,
  sendProposalMessageAndEmail,
} from "services/shareProposal";
import { useAppSelector } from "store";
import { deleteOffer } from "services/storeFront";
import { ErrorComponent } from "components/atoms/ErrorComponent";
import { mobileMaxWidth, tabletMaxWidth } from "components/atoms/GlobalStyles";
import { excludeBuilderOffers } from "utils/builderOffer";

type ShareProspectFormProps = {
  isPartner?: boolean;
  customerName: string;
  customerEmail?: string;
  customerPrimaryPhone?: string;
  offers: ListOffer[];
  onSubmitStart?: () => void;
  onSubmitComplete?: (
    type: "success" | "error" | "warning" | "info",
    message: string
  ) => void;
  prospectId: string;
  closeModal: () => void;
};
const ShareProspectForm = ({
  isPartner,
  customerName,
  customerEmail,
  customerPrimaryPhone,
  offers,
  onSubmitStart,
  onSubmitComplete,
  prospectId,
  closeModal,
}: ShareProspectFormProps) => {
  const cognitoToken = useAppSelector(
    (state) => state.auth.cognitoToken?.token
  );
  const hybridToken = useAppSelector((state) => state?.auth?.hybridToken);
  const [shareNote, setShareNote] = useState<string>("");
  const [error, setError] = useState<Error>();

  const shareableOffers = useMemo(
    () => excludeBuilderOffers(offers ?? []),
    [offers]
  );

  const offerOptions = useMemo(() => {
    return shareableOffers.map((e) => {
      return { label: e.label, value: e.id };
    });
  }, [shareableOffers]);
  const [selectedOffers, setSelectedOffers] = useState<string[]>(
    offerOptions.map((e) => e.value)
  );
  const [unCheckedOffers, setUnCheckedOffers] = useState<string[]>([]);

  const bannerMessage = useMemo(() => {
    if (isPartner) {
      return `${customerName} can download the Sunrun Customer App to view their offers and track their project.`;
    } else {
      return `${customerName} should download the Sunrun Customer App to receive & send messages, view their offers, and track their project.`;
    }
  }, [isPartner, customerName]);

  const onSelectOffer = useCallback(
    (offerId: string, selected: boolean) => {
      if (selected) {
        setSelectedOffers([...selectedOffers, offerId]);
        setUnCheckedOffers(unCheckedOffers.filter((item) => item !== offerId));
      } else {
        setSelectedOffers(selectedOffers.filter((item) => item !== offerId));
        setUnCheckedOffers([...unCheckedOffers, offerId]);
      }
    },
    [setSelectedOffers, selectedOffers, unCheckedOffers]
  );
  const onSend = useCallback(
    async (isPartner: boolean) => {
      if (!cognitoToken) {
        return;
      }
      if (selectedOffers.length === 0) {
        setError(new Error("No offer is selected!"));
      }
      onSubmitStart?.();
      // Remove not selected offers
      const selectedOfferObjects = shareableOffers.filter(
        (e) => !selectedOffers.includes(e.id)
      );
      const promises = [] as Promise<void>[];
      selectedOfferObjects.forEach((selectedOffer) => {
        promises.push(
          deleteOffer(cognitoToken, selectedOffer.id, selectedOffer.lockVersion)
        );
      });
      await Promise.all(promises);

      try {
        if (!isPartner) {
          await sendProposalMessageAndEmail(
            hybridToken,
            prospectId,
            shareNote,
            false
          );
          onSubmitComplete?.(
            "success",
            `${
              selectedOffers.length > 1 ? "Offers" : "Offer"
            } sent successfully.`
          );
        } else {
          await sendProposalEmail(hybridToken, prospectId, shareNote, "ONE");
          onSubmitComplete?.("success", "Email sent successfully.");
        }
      } catch (error) {
        onSubmitComplete?.("error", "Failed to share proposal.");
      }
    },
    [
      cognitoToken,
      selectedOffers,
      onSubmitStart,
      shareableOffers,
      hybridToken,
      prospectId,
      shareNote,
      onSubmitComplete,
    ]
  );

  const getUnCheckedOffersText = (offerOptions: any) => {
    let resp: string = "";
    if (unCheckedOffers.length === 1) {
      resp = offerOptions.find(
        (option: { value: string; label: string }) =>
          option.value === unCheckedOffers[0]
      ).label;
    } else {
      unCheckedOffers.map((unChecked, i) => {
        return (resp +=
          offerOptions.find(
            (option: { value: string; label: string }) =>
              option.value === unChecked
          ).label + (unCheckedOffers.length - 1 === i ? "" : " and "));
      });
    }
    return resp;
  };

  if (!offerOptions || offerOptions.length === 0) {
    return (
      <ErrorComponent
        context="ShareProspectForm"
        action="Share Prospect Load Offers"
        error={
          new Error(
            "Prospect cannot be shared, it does not contain any offers."
          )
        }
      />
    );
  }

  if (error) {
    return (
      <ErrorComponent
        context="ShareProspectForm"
        action="Share Prospect"
        error={error}
      />
    );
  }

  const oneOffer = offerOptions.length === 1;
  const singleUnCheckedOffer = unCheckedOffers.length === 1;
  return (
    <StyledShareOfferContainer>
      <Banner
        bannerType={BannerTypes.InfoLight}
        showClose={false}
        show={true}
        style={{
          border: "none",
          marginBottom: "12px",
        }}
      >
        {bannerMessage}
      </Banner>
      <ContentContainer>
        <ResponsiveContent>
          {!oneOffer && (
            <ModalSubTitle>
              <div>
                <Typography component="span" fontWeight={"medium"} size={20}>
                  Select offers to share
                </Typography>
              </div>
              <div>
                <Typography
                  component="span"
                  color={tokens.TINTS_OFF_WHITE_20}
                  size={16}
                >
                  Any offers that are not selected will be deleted.
                </Typography>
              </div>
            </ModalSubTitle>
          )}
          <CheckboxGridContainer>
            {oneOffer && (
              <div data-testid="share-one-offer-option">
                Share Offer <b>{offerOptions[0].label}</b> with{" "}
                <b>{customerName}</b>
              </div>
            )}
            {!oneOffer &&
              offerOptions.map((option) => {
                return (
                  <div key={option.value}>
                    <StyledCheckbox
                      data-testid={"checkbox-" + option.value}
                      label={option.label}
                      checked={selectedOffers.includes(option.value)}
                      onChange={(event) =>
                        onSelectOffer(option.value, event.target.checked)
                      }
                    />
                  </div>
                );
              })}
          </CheckboxGridContainer>
          {unCheckedOffers.length >= 1 && (
            <WarningMessage data-testid="warning-message">
              Option{singleUnCheckedOffer ? " " : "s "}
              <b>{getUnCheckedOffersText(offerOptions)}</b>
              {singleUnCheckedOffer ? " is " : " are "} going to be deleted.
            </WarningMessage>
          )}
          <NoteContainer>
            <label>
              <Typography component="span">Optional Note</Typography>
              <br />
              <Textarea
                onChange={(event) => setShareNote(event.target.value)}
              />
            </label>
          </NoteContainer>
        </ResponsiveContent>
        <ResponsiveContent>
          {(customerEmail || customerPrimaryPhone) && (
            <>
              <Typography component="span" fontWeight="medium" size={20}>
                Contact Information
              </Typography>
              <ContactInfo>
                <Typography component="span" fontWeight="medium">
                  {customerName}
                </Typography>
                <EmailAndPhoneContainer>
                  {customerPrimaryPhone && (
                    <ContactColumn>
                      <Typography
                        component="span"
                        color={tokens.TINTS_OFF_WHITE_20}
                      >
                        Phone
                      </Typography>
                      <Typography
                        component="span"
                        color={tokens.BRAND_HEROBLUE}
                      >
                        {customerPrimaryPhone}
                      </Typography>
                    </ContactColumn>
                  )}
                  {customerEmail && (
                    <ContactColumn>
                      <Typography
                        component="span"
                        color={tokens.TINTS_OFF_WHITE_20}
                      >
                        Email
                      </Typography>
                      <Typography
                        component="span"
                        color={tokens.BRAND_HEROBLUE}
                      >
                        {customerEmail}
                      </Typography>
                    </ContactColumn>
                  )}
                </EmailAndPhoneContainer>
              </ContactInfo>
            </>
          )}
        </ResponsiveContent>
      </ContentContainer>
      <ModalButtonContainer>
        <StyledButton color="secondary" onClick={closeModal}>
          Cancel
        </StyledButton>
        <StyledButton onClick={() => onSend(isPartner ?? false)}>
          {isPartner ? "Share by email" : "Share by email & messages"}
        </StyledButton>
      </ModalButtonContainer>
    </StyledShareOfferContainer>
  );
};

const StyledButton = styled(Button)`
  width: fit-content !important;
`;

const ContactInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  border: 1px solid ${tokens.TINTS_OFF_WHITE_40};
  background-color: ${tokens.TINTS_OFF_WHITE_80};
  border-radius: 8px;
  padding: 16px;
`;

const ContactColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex-grow: 1;
  min-width: 0;

  span {
    word-break: break-all;
    overflow-wrap: break-word;
    hyphens: auto;
  }
`;

const EmailAndPhoneContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 16px;
`;

const ContentContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 64px;

  @media (max-width: ${tabletMaxWidth}px) {
    flex-direction: column;
    gap: 12px;
  }
`;

const ResponsiveContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 50%;
  @media (max-width: ${tabletMaxWidth}px) {
    width: 100%;
  }
`;

const StyledShareOfferContainer = styled.div`
  color: ${tokens.BRAND_HEROBLUE};
`;

const CheckboxGridContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding-bottom: 16px;
`;

const StyledCheckbox = styled(Checkbox)`
  display: flex;
  align-items: center;

  input {
    width: 20px;
    height: 20px;
    margin-right: 8px;
  }
`;

const NoteContainer = styled.div`
  margin-top: 1rem;
`;

const WarningMessage = styled.div`
  background-color: ${tokens.ERROR_90};
  width: 95%;
  min-height: 20px;

  color: ${tokens.ERROR_50};
  padding: 14px;
`;

const ModalSubTitle = styled.div`
  padding-bottom: 8px;
  font-size: 16px;
  line-height: 24px;
`;

const ModalButtonContainer = styled.div`
  margin-top: 1rem;
  display: flex;
  gap: 16px;
  justify-content: flex-end;

  & > button {
    width: 155px;
    @media (max-width: ${tabletMaxWidth}px) {
      width: 100%;
    }
  }

  @media (max-width: ${mobileMaxWidth}px) {
    flex-direction: column;
  }
`;

export { ShareProspectForm };
